import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";

const SECTIONS = [
    {
        n: "01",
        h: "Information we collect",
        p: [
            "We collect three kinds of information.",
            "The first is what you give us directly — your name, email address, company, and message when you contact us, apply for a role, or subscribe to our newsletter. When you upload a résumé, we store the file itself and the URL that points to it.",
            "The second is what your browser sends us — IP address, user agent, referring page, language preference, and approximate location derived from the IP. These are ordinary parts of any HTTP request; we log them for security, rate-limiting, and troubleshooting.",
            "The third is what we observe about how our site is used — anonymized page views, search queries entered into the Insights index, and coarse engagement metrics. We do not attempt to attach these observations to any individual identity.",
        ],
    },
    {
        n: "02",
        h: "How we use it",
        p: [
            "We use the information you give us to reply to you, to consider your application, to send the newsletter you subscribed to, and to keep records of the conversations you started with us.",
            "We use technical information to secure our services against abuse, to keep the site available, and to understand — in aggregate — which of our writing is finding a readership.",
            "We do not sell personal information. We do not share personal information with advertisers. We do not build profiles for the purpose of targeted advertising.",
        ],
    },
    {
        n: "03",
        h: "Legal bases",
        p: [
            "For readers in the European Economic Area, the United Kingdom, and other jurisdictions with similar rules, we rely on the following legal bases:",
            "Consent — for newsletter subscription and other explicit opt-ins.",
            "Contract — where processing is necessary to respond to your request or evaluate your application.",
            "Legitimate interests — for the ordinary operation, security, and improvement of our site, where those interests are not overridden by your rights.",
            "Legal obligation — where retention or disclosure is required by applicable law.",
        ],
    },
    {
        n: "04",
        h: "Who we share with",
        p: [
            "We use a small set of processors, each chosen for its posture on privacy and security. These include our cloud infrastructure providers, our email delivery provider (for transactional and newsletter mail), and our object storage provider (for résumé files).",
            "We share information with these processors only to the extent necessary to operate the service, and they are contractually bound to protect it.",
            "We may disclose information when required by law, when necessary to protect our rights or the safety of others, or in connection with a merger, acquisition, or reorganization — in which case we will notify affected users.",
        ],
    },
    {
        n: "05",
        h: "International transfers",
        p: [
            "Our infrastructure is global. Data you send us may be transferred to and stored in countries other than the one you live in — including the United States and the European Economic Area.",
            "Where required, we rely on Standard Contractual Clauses or equivalent transfer mechanisms to protect the data during transfer.",
        ],
    },
    {
        n: "06",
        h: "Retention",
        p: [
            "We keep information only for as long as we need it.",
            "Contact submissions and applications are retained for up to 24 months after the last interaction, unless you ask us to delete them sooner.",
            "Newsletter subscriptions are retained until you unsubscribe. Every newsletter includes an unsubscribe link.",
            "Server logs are retained for up to 90 days for security and troubleshooting purposes.",
        ],
    },
    {
        n: "07",
        h: "Your rights",
        p: [
            "You may ask us to confirm what personal information we hold about you, to correct it, to delete it, to restrict how we use it, or to send you a portable copy.",
            "You may also withdraw consent for any processing that relies on it — this will not affect the lawfulness of processing carried out before withdrawal.",
            "To exercise any of these rights, email privacy@iventara.com. We will respond within thirty days.",
            "If you believe we have not handled your request appropriately, you may lodge a complaint with your local data protection authority.",
        ],
    },
    {
        n: "08",
        h: "Cookies",
        p: [
            "We use a small number of first-party cookies to remember your language preference, your theme (light or dark), and your session state. We do not use third-party advertising cookies.",
            "Your browser lets you refuse or delete cookies. Doing so may cause parts of the site to behave less well, but nothing essential will break.",
        ],
    },
    {
        n: "09",
        h: "Children",
        p: [
            "Our site is not directed at children under 16, and we do not knowingly collect personal information from them. If you believe a child has provided us information, please email privacy@iventara.com and we will delete it.",
        ],
    },
    {
        n: "10",
        h: "Changes to this policy",
        p: [
            "We may update this policy from time to time. If we make material changes, we will update the effective date at the top of this page and, where appropriate, notify subscribers by email.",
        ],
    },
    {
        n: "11",
        h: "Contact",
        p: [
            "Questions about privacy at Ventara go to privacy@iventara.com. For general inquiries, hello@iventara.com.",
        ],
    },
];

export default function Privacy() {
    return (
        <Layout title="Privacy — Ventara" description="How Ventara collects, uses, and protects personal information.">
            <PageHero
                eyebrow="Legal · Effective February 2026"
                title={<>A short policy,<br />written plainly.</>}
                lead="We think privacy notices should be readable. Below is what we collect, how we use it, and what you can ask us to do about it."
            />
            <Section className="pb-32">
                <div className="divide-y divide-border border-t border-b border-border max-w-4xl">
                    {SECTIONS.map((s) => (
                        <Reveal key={s.n}>
                            <div className="grid lg:grid-cols-12 gap-8 py-10" data-testid={`privacy-${s.n}`}>
                                <div className="lg:col-span-2 label pt-2">{s.n}</div>
                                <div className="lg:col-span-10">
                                    <h2 className="font-serif text-3xl tracking-tight">{s.h}</h2>
                                    <div className="prose-body mt-4 space-y-3 max-w-2xl">
                                        {s.p.map((line, i) => (<p key={i}>{line}</p>))}
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
